import {
  getFontCss,
  styleConstants,
  themeConstants,
} from '@paywithextend/components'
import React, {
  HTMLInputTypeAttribute,
  useEffect,
  useRef,
  useState,
} from 'react'
import ErrorList from 'src/components/ErrorList/ErrorList'
import FieldLabel from 'src/components/FieldLabel/FieldLabel'
import Trns from 'src/localization/Trns'
import styled, { css } from 'styled-components'

// TODO: remove this whole prop. Old is not being used any more
export enum TextInputFieldDesigns {
  Old = 'old',
  New = 'new',
}

interface TextInputFieldProps {
  design: TextInputFieldDesigns
  errors?: Array<React.ReactElement | string>
  fieldLabel?: React.ReactElement | string
  fieldLabelTestId?: string
  fieldLabelAriaLabel?: string
  showUpdates?: boolean
  hideWasOnUpdate?: boolean
  baseValue?: string
  value?: string | number
  onFocus?: (e: React.SyntheticEvent) => void
  onBlur?: () => void
  onChange?: (value: string) => void
  onKeyDown?: (e: React.KeyboardEvent<HTMLInputElement>) => void
  placeholder: string
  type?: HTMLInputTypeAttribute
  disabled?: boolean
  className?: string
  autoComplete?: string
  name?: string
  isOptional?: boolean
  // TODO: see if this lives somewhere
  inputMode?:
    | 'none'
    | 'text'
    | 'tel'
    | 'url'
    | 'email'
    | 'numeric'
    | 'decimal'
    | 'search'
  allCaps?: boolean
  id?: string
  testId?: string
  characterLimit?: number
  hideCharacterLimitCount?: boolean
  disableWheel?: boolean
  step?: number
  disablePositiveAndNegative?: boolean
  disableExponent?: boolean
  disableDecimal?: boolean
  rightImage?: React.ReactElement
  leftImage?: React.ReactElement
  tooltip?: React.ReactElement
  hideToolipOnUpdate?: boolean
  isStatic?: boolean
}

const LeftImage = styled.div`
  display: flex;
  align-items: center;
  margin-left: 20px;
`

const TextInputField: React.FC<TextInputFieldProps> = (
  props,
): React.ReactElement => {
  const [showErrors, setShowErrors] = useState(props.errors !== undefined)
  const ref = useRef<HTMLInputElement | null>(null)
  useEffect((): void => {
    setShowErrors(props.errors !== undefined)
  }, [props.errors])

  const onChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
  ): void => {
    setShowErrors(false)
    props.onChange !== undefined && props.onChange(e.target.value)
  }

  const onKeyPress = (e: React.KeyboardEvent<HTMLInputElement>): void => {
    if (e.key === 'Enter') {
      e.preventDefault()
      if (document.activeElement === ref.current) {
        ref.current?.blur()
      }
    }
  }

  const onKeyDown = (event: React.KeyboardEvent<HTMLInputElement>): void => {
    props.onKeyDown?.(event)
    props.disableExponent &&
      ['e', 'E'].includes(event.key) &&
      event.preventDefault()
    props.disablePositiveAndNegative &&
      ['+', '-'].includes(event.key) &&
      event.preventDefault()
    props.disableDecimal && event.key === '.' && event.preventDefault()
  }

  const isUpdated = !!props.showUpdates && props.baseValue !== props.value

  return (
    <div className={props.className}>
      {props.fieldLabel !== undefined && (
        <FieldLabel
          isUpdated={isUpdated}
          css={css`
            display: flex;
            align-items: center;
          `}
          dontHideOverflow
          testId={props.testId}
          ariaLabel={props.fieldLabelAriaLabel}
        >
          {!isUpdated || props.hideWasOnUpdate ? (
            !props.isOptional ? (
              props.fieldLabel
            ) : (
              <Trns
                source="${fieldLabel} (optional)"
                expressions={{
                  fieldLabel: (
                    <span
                      css={css`
                        margin-right: 4px;
                      `}
                    >
                      {props.fieldLabel}
                    </span>
                  ),
                }}
              />
            )
          ) : props.baseValue === undefined ? (
            props.fieldLabel
          ) : (
            <Trns
              source='${fieldLabel} (was "${baseValue}")'
              expressions={{
                fieldLabel: props.fieldLabel,
                baseValue: props.baseValue,
              }}
            />
          )}
          {props.tooltip !== undefined &&
            (!isUpdated || (isUpdated && !props.hideToolipOnUpdate)) &&
            props.tooltip}
        </FieldLabel>
      )}
      {props.isStatic && <div>{props.value ?? '--'}</div>}
      {!props.isStatic && (
        <>
          <div
            css={css`
              ${props.fieldLabel !== undefined &&
              css`
                margin-top: 6px;
              `}
              position: relative;
              display: flex;
              align-items: center;
              border-radius: 6px;
              border: 1px solid ${styleConstants.colors.gray_medium};
              background-color: ${styleConstants.colors.gray_lightest};
              overflow: hidden;

              &:focus,
              &:focus-within,
              &:hover {
                ${!props.disabled &&
                css`
                  background-color: ${styleConstants.colors.white};
                  border: 1px solid ${themeConstants.colors.cta};
                `}
              }

              &:hover > #${props.id ? props.id + 'Pwd' : 'pwd'} {
                background-color: ${styleConstants.colors.white};
              }

              ${showErrors &&
              css`
                border: 1px solid ${styleConstants.colors.red};
                ${getFontCss(
                  14,
                  styleConstants.colors.red,
                  styleConstants.fontFamilies.regular,
                )};

                &:focus,
                &:hover {
                  border: 1px solid ${styleConstants.colors.red};
                }
              `}
            `}
          >
            {props.leftImage && <LeftImage>{props.leftImage}</LeftImage>}
            <input
              css={css`
                width: 100%;
                border-radius: 6px;
                border: none;
                padding: 20px 20px 20px ${props.leftImage ? '8px' : '20px'};
                max-height: 50px;
                caret-color: ${themeConstants.colors.cta};
                ${getFontCss(
                  14,
                  themeConstants.colors.font_primary,
                  styleConstants.fontFamilies.regular,
                )};
                background-color: ${styleConstants.colors.gray_lightest};

                &::placeholder {
                  opacity: 0.5;
                  ${getFontCss(
                    14,
                    styleConstants.colors.gray_darkest,
                    styleConstants.fontFamilies.regular,
                  )};
                }

                &:focus,
                &:hover {
                  background-color: ${styleConstants.colors.white};
                  border: none;
                }

                ${isUpdated &&
                css`
                  border: 1px solid ${themeConstants.colors.highlight};
                  ${getFontCss(
                    14,
                    themeConstants.colors.highlight,
                    styleConstants.fontFamilies.regular,
                  )};

                  &:focus,
                  &:hover {
                    border: 1px solid ${themeConstants.colors.highlight};
                  }
                `}

                ${!!props.disabled &&
                css`
                  opacity: 0.5;
                  pointer-events: none;
                `}
    
              ${!!props.allCaps &&
                css`
                  text-transform: uppercase;

                  &::placeholder {
                    text-transform: none;
                  }
                `}
    
              &:-webkit-autofill,
              &:-webkit-autofill:hover,
              &:-webkit-autofill:focus,
              &:-webkit-autofill:active {
                  transition: background-color 5000s;
                }
              `}
              placeholder={props.placeholder}
              value={props.value}
              type={props.type || 'text'}
              onChange={onChange}
              onKeyDown={onKeyDown}
              onFocus={props.onFocus}
              onBlur={props.onBlur}
              onKeyPress={onKeyPress}
              disabled={props.disabled}
              autoComplete={props.autoComplete || 'off'}
              name={props.name}
              inputMode={props.inputMode}
              ref={ref}
              id={props.id}
              maxLength={props.characterLimit}
              onWheel={(e): void => {
                props.disableWheel && e.currentTarget.blur()
              }}
              step={props.step}
              data-testid={
                props.testId
                  ? `${props.testId}-input`
                  : typeof props.fieldLabel === 'string'
                    ? `${props.fieldLabel.toLowerCase().replace(/\s+/g, '-')}-input`
                    : props.id
                      ? `${props.id.toLowerCase().replace(/\s+/g, '-')}-input`
                      : ''
              }
            />
            {props.rightImage}
          </div>
          {typeof props.value === 'string' &&
            props.characterLimit !== undefined &&
            !props.hideCharacterLimitCount && (
              <span
                css={css`
                  align-self: end;
                  margin-top: 10px;
                  ${getFontCss(
                    12,
                    styleConstants.colors.gray_darkest,
                    styleConstants.fontFamilies.regular,
                  )}
                `}
              >
                {`${props.value ? props.value.length : 0} / ${props.characterLimit}`}
              </span>
            )}
          {props.errors !== undefined && showErrors && (
            <ErrorList errors={props.errors} />
          )}
        </>
      )}
    </div>
  )
}

export default React.memo(TextInputField)
